import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { RecommendationSource } from '~/api/Catalog';
import RecommendationSection from '~/components/isolated/CategoryBlocks/RecommendationSection';
import ProductsGrid from '~/components/isolated/ProductsGrid';
import ProductCardSkeletonFactory from '~/components/ProductCardV3/skeletons/ProductCardSkeletonFactory';
import { useGlobal } from '~/hooks/useGlobal';
import { cartStore, catalogStore } from '~/stores';
import {
  ORDER_AGAIN_CONFIG,
  orderAgainStore,
} from '~/stores/RecommendationsStore';

import styles from './CartV3.module.scss';
import CartContentV3 from './components/CartContentV3';
import EmptyCartV3 from './components/EmptyCartV3';
import { useUserVerificationStatusChecker } from './hooks';

/**
 * Main Cart component V3 with new design system
 * Uses ProductCardC horizontal cards and OrderAgainSection slider
 */
const CartV3 = () => {
  useUserVerificationStatusChecker();
  const { t } = useTranslation();
  const { isMobile } = useGlobal();

  useEffect(() => {
    cartStore.enableCalculate();
    return () => cartStore.disableCalculate();
  }, []);

  const isEmpty = cartStore.cart.length === 0;

  const recommendations =
    catalogStore.productRecommendations[RecommendationSource.CART]?.data || [];

  const isLoading = catalogStore.productRecommendationsMeta.isLoading;

  const skeletonComponent = isLoading ? (
    <div className={styles.skeletonGrid}>
      {Array.from({ length: isMobile ? 4 : 5 }, (_, i) => (
        <ProductCardSkeletonFactory key={`skeleton-${i}`} variant="m" />
      ))}
    </div>
  ) : undefined;

  return (
    <div className={styles.cartPage}>
      <RecommendationSection
        store={orderAgainStore}
        config={ORDER_AGAIN_CONFIG}
      />

      {isEmpty ? <EmptyCartV3 /> : <CartContentV3 />}

      {(recommendations.length > 0 || isLoading) && (
        <ProductsGrid
          title={t('beInterested')}
          items={recommendations}
          skeletonComponent={skeletonComponent}
        />
      )}
    </div>
  );
};

export default observer(CartV3);
